import { FC, useState } from 'react';
import { useUnifiedAdminMetrics } from '@/hooks/useUnifiedAdminMetrics';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Users2, DollarSign, TrendingDown, ArrowUpRight, 
  RefreshCw, Calendar, ChevronDown, Activity, Clock, LineChart
} from 'lucide-react';
import { format, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DateRange } from 'react-day-picker';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { toast } from '@/components/ui/use-toast';
import { AdminUsageChart } from './stats/AdminUsageChart';
import { UserEngagement } from './stats/UserEngagement';
import { UserRetention } from './stats/UserRetention';

export const UnifiedAdminDashboard: FC = () => {
  // Estado para el filtro de fechas
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subMonths(new Date(), 1),
    to: new Date(),
  });

  // Usar el hook de métricas unificadas
  const { 
    data: metrics, 
    isLoading, 
    error, 
    refreshMetrics 
  } = useUnifiedAdminMetrics({
    startDate: dateRange?.from,
    endDate: dateRange?.to,
  });

  // Manejar errores
  if (error) {
    console.error('Error loading unified metrics:', error);
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-semibold text-red-700 mb-2">Error al cargar las métricas</h3>
        <p className="text-red-600">{(error as Error).message}</p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={() => refreshMetrics()}
        >
          <RefreshCw className="h-4 w-4 mr-2" /> Reintentar
        </Button>
      </div>
    );
  }

  // Manejar la actualización manual de datos
  const handleRefresh = async () => {
    try {
      await refreshMetrics();
      toast({
        title: "Métricas actualizadas",
        description: "Los datos del dashboard han sido actualizados correctamente.",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error al actualizar",
        description: "No se pudieron actualizar las métricas. Por favor, intente nuevamente.",
      });
    }
  };

  return (
    <div className="space-y-8">
      {/* Cabecera con filtros y botón de actualización */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-semibold text-gray-800">Métricas del Panel</h2>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            locale={es}
            placeholder="Seleccionar período"
            className="w-full sm:w-auto"
            calendarClassName="bg-white shadow-lg border border-gray-200 rounded-md"
          />
          
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Métricas Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Usuarios Activos */}
        <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Usuarios Activos</CardTitle>
            <Users2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {metrics?.main_stats.active_users || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  suscripciones premium activas
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* MRR */}
        <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">MRR</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  ${metrics?.main_stats.mrr.toLocaleString('es-AR', { maximumFractionDigits: 0 }) || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  ingreso mensual recurrente
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Conversión Trial */}
        <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Conversión Trial</CardTitle>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {metrics?.main_stats.conversion_rate.toFixed(1) || 0}%
                </div>
                <p className="text-xs text-muted-foreground">
                  tasa de conversión de prueba
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Churn Rate */}
        <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Churn Rate</CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {metrics?.main_stats.churn_rate.toFixed(1) || 0}%
                </div>
                <p className="text-xs text-muted-foreground">
                  tasa de cancelación mensual
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Gráfico de Uso */}
      <div className="mb-8">
        <Card className="bg-white shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle>Actividad de Usuarios</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <AdminUsageChart />
          </CardContent>
        </Card>
      </div>

      {/* Embudo de Conversión */}
      <Card className="bg-white shadow-lg mb-8">
        <CardHeader className="pb-2">
          <CardTitle>Embudo de Conversión</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Visitas */}
              <div className="bg-blue-100 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Users2 className="h-5 w-5 text-blue-600" />
                    <span className="font-medium">Visitas</span>
                  </div>
                  <span className="font-bold text-lg">{metrics?.funnel.total_visits || 0}</span>
                </div>
              </div>

              {/* Registros */}
              <div className="bg-indigo-100 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Users2 className="h-5 w-5 text-indigo-600" />
                    <span className="font-medium">Registros</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-lg">{metrics?.funnel.total_registrations || 0}</span>
                    <div className="text-xs text-gray-500">
                      {(metrics?.funnel.registration_rate || 0).toFixed(1)}% de las visitas
                    </div>
                  </div>
                </div>
              </div>

              {/* Emails Confirmados */}
              <div className="bg-purple-100 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Users2 className="h-5 w-5 text-purple-600" />
                    <span className="font-medium">Emails Confirmados</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-lg">{metrics?.funnel.total_confirmed_emails || 0}</span>
                    <div className="text-xs text-gray-500">
                      {(metrics?.funnel.email_confirmation_rate || 0).toFixed(1)}% de los registros
                    </div>
                    <div className="text-xs text-blue-600">
                      {(metrics?.funnel.visits_to_confirmed_email_rate || 0).toFixed(1)}% de las visitas
                    </div>
                  </div>
                </div>
              </div>

              {/* En Trial */}
              <div className="bg-violet-100 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Users2 className="h-5 w-5 text-violet-600" />
                    <span className="font-medium">En Trial</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-lg">{metrics?.funnel.total_in_trial || 0}</span>
                    <div className="text-xs text-gray-500">
                      {(metrics?.funnel.trial_conversion_rate || 0).toFixed(1)}% de emails confirmados
                    </div>
                    <div className="text-xs text-blue-600">
                      {(metrics?.funnel.visits_to_trial_rate || 0).toFixed(1)}% de las visitas
                    </div>
                  </div>
                </div>
              </div>

              {/* En Período de Gracia */}
              <div className="bg-amber-100 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Users2 className="h-5 w-5 text-amber-600" />
                    <span className="font-medium">En Período de Gracia</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-lg">{metrics?.funnel.total_in_grace_period || 0}</span>
                    <div className="text-xs text-gray-500">
                      {(metrics?.funnel.grace_period_rate || 0).toFixed(1)}% de emails confirmados
                    </div>
                    <div className="text-xs text-blue-600">
                      {(metrics?.funnel.visits_to_grace_period_rate || 0).toFixed(1)}% de las visitas
                    </div>
                  </div>
                </div>
              </div>

              {/* Premium Activo */}
              <div className="bg-green-100 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Users2 className="h-5 w-5 text-green-600" />
                    <span className="font-medium">Premium Activo</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-lg">{metrics?.funnel.total_premium_active || 0}</span>
                    <div className="text-xs text-gray-500">
                      {(metrics?.funnel.premium_conversion_rate || 0).toFixed(1)}% de todos los usuarios con email confirmado
                    </div>
                    <div className="text-xs text-blue-600">
                      {(metrics?.funnel.visits_to_premium_rate || 0).toFixed(1)}% de las visitas
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Métricas de Suscripción */}
      <Card className="bg-white shadow-lg mb-8">
        <CardHeader className="pb-2">
          <CardTitle>Métricas de Suscripción</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* LTV Promedio */}
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">LTV Promedio</p>
                  <p className="text-xl font-semibold">
                    ${Math.round(metrics?.subscription_metrics.average_ltv || 0).toLocaleString('es-AR')}
                  </p>
                </div>
              </div>

              {/* Suscriptores Activos */}
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Users2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Suscriptores Activos</p>
                  <p className="text-xl font-semibold">
                    {metrics?.subscription_metrics.total_active_subscribers || 0}
                  </p>
                </div>
              </div>

              {/* Duración Promedio */}
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white rounded-full border border-gray-200">
                  <Calendar className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Duración Promedio</p>
                  <p className="text-xl font-semibold">
                    {Math.round(metrics?.subscription_metrics.average_subscription_length || 0)} meses
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Grid de Engagement y Retención */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <Card className="bg-white shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle>Engagement de Usuarios</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <UserEngagement />
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle>Retención de Usuarios</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <UserRetention />
          </CardContent>
        </Card>
      </div>

      {/* Información de Actualización */}
      {metrics && (
        <div className="text-xs text-gray-500 text-right">
          Última actualización: {new Date(metrics.metadata.generated_at).toLocaleString('es-AR')}
        </div>
      )}
    </div>
  );
};
